import React from 'react';
import type { HubCrossLink } from '../types/hub-cross-link';
import type { LocationTrendsData } from '../hooks/useLocationTrends';
import { useMarketHeaderData } from '../hooks/useMarketHeaderData';
import { MarketHeaderSeoBlocks } from './MarketHeaderSeoBlocks';
import { MarketRail } from './MarketRail';
import { MarketStatCards } from './MarketStatCards';

export interface MarketHeaderProps {
  location: string;
  /** Locations that currently have a background image (static list plus Lambda-discovered ones). */
  knownLocationBackgrounds?: string[];
  nbHits: number;
  description?: string;
  backgroundImage?: string; // Optional override, otherwise auto-detected from location
  locationTrendsData?: LocationTrendsData | null;
  /** Cross-links to the city's role x city pages and employer/role/salary hubs. */
  hubLinks?: HubCrossLink[];
}

/**
 * MarketHeader — composes the shared data hook with the two responsive presentations:
 * MarketRail (desktop, sticky beside results) and MarketStatCards (mobile, title + swipeable
 * stat cards + disclosure). Renders MarketHeaderSeoBlocks once so the JSON-LD and sr-only
 * content aren't duplicated between the two viewport-gated presentations. No wrapping element,
 * so this doesn't add a DOM level the server render didn't have (SrpView.tsx renders and
 * hydrates the same tree, see its header comment).
 */
export const MarketHeader: React.FC<MarketHeaderProps> = ({
  location,
  knownLocationBackgrounds,
  nbHits,
  description,
  backgroundImage,
  locationTrendsData,
  hubLinks,
}) => {
  const data = useMarketHeaderData({
    location,
    knownLocationBackgrounds,
    nbHits,
    description,
    backgroundImage,
    locationTrendsData,
  });

  return (
    <>
      <MarketHeaderSeoBlocks data={data} />
      <MarketRail data={data} hubLinks={hubLinks} />
      <MarketStatCards data={data} hubLinks={hubLinks} />
    </>
  );
};
